import React, { useState, useRef } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';

// Mock data matching the screenshot
const mockData = [
    { name: 'Sandwitch – 43646 0\n(Single)', units: 131 },
    { name: 'BENTONVILLE BREWING GRAPEFRUIT\nWRECKER 6PK – 666628957708\n(Single)', units: 56 },
    { name: 'Body Amor Water –\n858176002140\n(Single)', units: 46 },
    { name: 'HIGHNOON WATERMELON VODKA &\nSODA 4 PK – 085000429259 (Single)', units: 39 },
    { name: '$10K STACKS –\n814542018016\n(Single)', units: 12 },
];

// Custom X-axis tick with line-wrapped text
const CustomXAxisTick = ({ x, y, payload }) => {
    const lines = payload.value.split('\n');
    return (
        <g transform={`translate(${x},${y})`}>
            {lines.map((line, i) => (
                <text
                    key={i}
                    x={0}
                    y={0}
                    dy={14 + i * 13}
                    textAnchor="middle"
                    fill="var(--etpos-text-muted, #64748b)"
                    fontSize={11}
                >
                    {line}
                </text>
            ))}
        </g>
    );
};

// ─── Helper: get the SVG element from the chart container ───────────────────
const getChartSvg = (chartRef) => {
    if (!chartRef.current) return null;
    return chartRef.current.querySelector('svg');
};

// ─── Helper: convert SVG element to a canvas, then return the canvas ────────
const svgToCanvas = (svgEl, scale = 2) => {
    return new Promise((resolve, reject) => {
        try {
            // Use getBoundingClientRect for the true rendered dimensions
            // (recharts SVG has no explicit width/height attrs, so clientWidth/Height = 0)
            const rect = svgEl.getBoundingClientRect();
            const w = Math.round(rect.width)  || svgEl.parentElement?.offsetWidth  || 800;
            const h = Math.round(rect.height) || svgEl.parentElement?.offsetHeight || 400;

            // Clone the SVG and stamp explicit width/height so the browser can render it
            const clone = svgEl.cloneNode(true);
            clone.setAttribute('width',  w);
            clone.setAttribute('height', h);

            const svgData = new XMLSerializer().serializeToString(clone);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width  = w * scale;
                canvas.height = h * scale;
                const ctx = canvas.getContext('2d');
                ctx.scale(scale, scale);
                // White background so PNG/JPEG aren't transparent
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0, w, h);
                URL.revokeObjectURL(url);
                resolve(canvas);
            };
            img.onerror = reject;
            img.src = url;
        } catch (err) {
            reject(err);
        }
    });
};

const TrendingProductsReport = () => {
    const [showChartMenu, setShowChartMenu] = useState(false);
    const chartRef = useRef(null);
    const cardRef = useRef(null);

    // ── Full Screen ────────────────────────────────────────────────────────
    const handleFullScreen = () => {
        const el = cardRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            el.requestFullscreen().catch(() => {});
        } else {
            document.exitFullscreen().catch(() => {});
        }
        setShowChartMenu(false);
    };

    // ── Print ──────────────────────────────────────────────────────────────
    const handlePrintChart = () => {
        window.print();
        setShowChartMenu(false);
    };

    // ── Download PNG ───────────────────────────────────────────────────────
    const handleDownloadPNG = async () => {
        setShowChartMenu(false);
        const svg = getChartSvg(chartRef);
        if (!svg) return;
        try {
            const canvas = await svgToCanvas(svg, 2);
            const link = document.createElement('a');
            link.download = 'trending-products-chart.png';
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (e) {
      /* console.error('PNG download failed:', e); */
        }
    };

    // ── Download JPEG ──────────────────────────────────────────────────────
    const handleDownloadJPEG = async () => {
        setShowChartMenu(false);
        const svg = getChartSvg(chartRef);
        if (!svg) return;
        try {
            const canvas = await svgToCanvas(svg, 2);
            const link = document.createElement('a');
            link.download = 'trending-products-chart.jpg';
            link.href = canvas.toDataURL('image/jpeg', 0.95);
            link.click();
        } catch (e) {
      /* console.error('JPEG download failed:', e); */
        }
    };

    // ── Download PDF ───────────────────────────────────────────────────────
    const handleDownloadPDF = async () => {
        setShowChartMenu(false);
        const svg = getChartSvg(chartRef);
        if (!svg) return;
        try {
            // jsPDF is already installed in this project
            const { jsPDF } = await import('jspdf');
            const canvas = await svgToCanvas(svg, 2);
            const imgData = canvas.toDataURL('image/png');

            const pdfW = canvas.width / 2;   // back to CSS px
            const pdfH = canvas.height / 2;

            // Landscape if wider than taller
            const orientation = pdfW > pdfH ? 'l' : 'p';
            const pdf = new jsPDF({ orientation, unit: 'px', format: [pdfW, pdfH] });
            pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
            pdf.save('trending-products-chart.pdf');
        } catch (e) {
      /* console.error('PDF download failed:', e); */
        }
    };

    // ── Download SVG ───────────────────────────────────────────────────────
    const handleDownloadSVG = () => {
        setShowChartMenu(false);
        const svg = getChartSvg(chartRef);
        if (!svg) return;
        const svgData = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = 'trending-products-chart.svg';
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Top Trending Products Chart Card ── */}
            <div
                ref={cardRef}
                style={{
                    background: 'var(--etpos-card, #fff)',
                    border: '1px solid var(--etpos-border, #e2e8f0)',
                    borderRadius: '10px',
                    padding: '1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    position: 'relative',
                }}
            >

                {/* Card Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: '700', fontSize: '1rem', color: 'var(--etpos-text, #1e293b)' }}>
                            Top Trending Products
                        </span>
                        <i className="fa fa-info-circle" style={{ color: '#3b82f6', fontSize: '0.9rem', cursor: 'pointer' }} title="Based on total units sold" />
                    </div>

                    {/* Chart burger menu */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowChartMenu(!showChartMenu)}
                            style={{
                                background: 'none',
                                border: '1px solid var(--etpos-border, #e2e8f0)',
                                borderRadius: '4px',
                                padding: '0.3rem 0.5rem',
                                cursor: 'pointer',
                                color: 'var(--etpos-text-muted, #64748b)',
                                lineHeight: 1,
                                fontSize: '1rem',
                            }}
                            title="Chart options"
                        >
                            ☰
                        </button>

                        {showChartMenu && (
                            <>
                                {/* Backdrop */}
                                <div
                                    style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                                    onClick={() => setShowChartMenu(false)}
                                />
                                <div style={{
                                    position: 'absolute', top: '110%', right: 0,
                                    background: 'var(--etpos-card, #fff)',
                                    border: '1px solid var(--etpos-border, #e2e8f0)',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                    minWidth: '190px',
                                    zIndex: 100,
                                    padding: '0.25rem 0',
                                }}>
                                    {[
                                        { icon: 'fa-arrows-alt',    label: 'View in full screen',       action: handleFullScreen },
                                        { icon: 'fa-print',         label: 'Print chart',               action: handlePrintChart },
                                        null, // divider
                                        { icon: 'fa-file-image-o',  label: 'Download PNG image',        action: handleDownloadPNG },
                                        { icon: 'fa-file-image-o',  label: 'Download JPEG image',       action: handleDownloadJPEG },
                                        { icon: 'fa-file-pdf-o',    label: 'Download PDF document',     action: handleDownloadPDF },
                                        { icon: 'fa-code',          label: 'Download SVG vector image', action: handleDownloadSVG },
                                    ].map((item, i) =>
                                        item === null ? (
                                            <hr key={i} style={{ margin: '0.25rem 0', border: 'none', borderTop: '1px solid var(--etpos-border, #f1f5f9)' }} />
                                        ) : (
                                            <button
                                                key={i}
                                                onClick={item.action}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                                                    width: '100%', background: 'none', border: 'none',
                                                    padding: '0.5rem 1rem', fontSize: '0.8125rem',
                                                    color: 'var(--etpos-text, #334155)', cursor: 'pointer', textAlign: 'left',
                                                    transition: 'background 0.15s',
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'var(--etpos-bg, #f8fafc)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                            >
                                                <i className={`fa ${item.icon}`} style={{ width: '14px', color: 'var(--etpos-text-muted, #64748b)' }} />
                                                {item.label}
                                            </button>
                                        )
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Y-axis label + chart */}
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {/* Rotated Y-axis label */}
                    <div style={{
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        fontSize: '0.8rem',
                        color: 'var(--etpos-text-muted, #64748b)',
                        marginRight: '0.5rem',
                        alignSelf: 'center',
                        userSelect: 'none',
                    }}>
                        Values
                    </div>

                    {/* Chart */}
                    <div ref={chartRef} style={{ flex: 1, minHeight: 320 }}>
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart
                                data={mockData}
                                margin={{ top: 10, right: 20, left: 0, bottom: 80 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--etpos-border, #f1f5f9)" />
                                <XAxis
                                    dataKey="name"
                                    tick={<CustomXAxisTick />}
                                    interval={0}
                                    height={90}
                                    axisLine={{ stroke: 'var(--etpos-border, #e2e8f0)' }}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: 'var(--etpos-text-muted, #64748b)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    domain={[0, 150]}
                                    ticks={[0, 25, 50, 75, 100, 125, 150]}
                                />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: '8px',
                                        border: '1px solid var(--etpos-border, #e2e8f0)',
                                        backgroundColor: 'var(--etpos-card, #fff)',
                                        color: 'var(--etpos-text, #1e293b)',
                                        fontSize: '0.8125rem',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    }}
                                    formatter={val => [`${val} units`, 'Total unit sold']}
                                    labelFormatter={label => label.replace(/\n/g, ' ')}
                                />
                                <Legend
                                    wrapperStyle={{ paddingTop: '1rem', fontSize: '0.8125rem', color: 'var(--etpos-text-muted, #64748b)' }}
                                    formatter={() => 'Total unit sold'}
                                />
                                <Bar
                                    dataKey="units"
                                    name="Total unit sold"
                                    fill="#60a5fa"
                                    radius={[3, 3, 0, 0]}
                                    maxBarSize={80}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrendingProductsReport;
